import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, Instagram } from "lucide-react";
import Logo from "../components/Logo";
import { NAV_LINKS, ORG } from "../data/content";
import { useScrollToSection, scrollToSection, openWhatsApp } from "../utils/scroll";

const ease = [0.22, 1, 0.36, 1];

export default function Navbar({ introDone }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const scrollTo = useScrollToSection();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.documentElement.style.overflow = open ? "hidden" : "";
    if (open) window.lenis?.stop();
    else window.lenis?.start();
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [open]);

  const go = (e, href) => {
    setOpen(false);
    setTimeout(() => scrollTo(e, href), open ? 250 : 0);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={introDone ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.7, ease }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav shadow-[0_8px_30px_rgba(13,75,86,0.10)]" : "bg-transparent"
      }`}
    >
      <nav
        className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-12"
        aria-label="Main navigation"
      >
        <a href="#home" onClick={(e) => go(e, "#home")} className="transition-transform duration-300 hover:scale-[1.03]">
          <Logo />
        </a>

        <ul className="hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((link) => (
            <li key={link.label}>
              <a
                href={link.href}
                data-testid={`nav-link-${link.href.replace("#", "")}`}
                onClick={(e) => go(e, link.href)}
                className={`group relative text-sm font-semibold transition-colors ${
                  scrolled ? "text-[#0D4B56]" : "text-white/90"
                } hover:text-[#D4AF37]`}
              >
                {link.label}
                <span className="absolute -bottom-1.5 left-0 h-0.5 w-0 rounded-full bg-[#D4AF37] transition-all duration-300 group-hover:w-full" />
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <a
            href="#get-involved"
            data-testid="nav-support-button"
            onClick={(e) => go(e, "#get-involved")}
            className="hidden rounded-full gold-shimmer px-6 py-2.5 text-sm font-bold text-[#08353D] shadow-[0_8px_24px_rgba(212,175,55,0.35)] transition-transform duration-300 hover:-translate-y-0.5 hover:scale-[1.02] sm:inline-flex"
          >
            Support Us
          </a>
          <button
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-hamburger"
            className={`inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors lg:hidden ${
              scrolled || open ? "bg-[#0D4B56] text-white" : "bg-white/15 text-white backdrop-blur"
            }`}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.3, ease }}
            className="glass-nav fixed inset-x-0 top-[76px] bottom-0 z-40 overflow-y-auto px-6 pb-10 pt-6 lg:hidden"
            data-testid="mobile-drawer"
          >
            <ul className="flex flex-col gap-2">
              {NAV_LINKS.map((link, i) => (
                <motion.li
                  key={link.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.35, ease }}
                >
                  <a
                    href={link.href}
                    onClick={(e) => go(e, link.href)}
                    className="block rounded-2xl px-4 py-4 font-display text-2xl font-bold text-[#0D4B56] transition-colors hover:bg-[#E6F0F2]"
                  >
                    {link.label}
                  </a>
                </motion.li>
              ))}
            </ul>
            <a
              href="#get-involved"
              onClick={(e) => go(e, "#get-involved")}
              className="mt-6 flex w-full items-center justify-center rounded-full gold-shimmer px-6 py-4 text-base font-bold text-[#08353D]"
              data-testid="mobile-support-button"
            >
              Support Us
            </a>
            <div className="mt-8 flex items-center justify-center gap-4">
              <a
                href={ORG.phoneTel}
                data-testid="nav-mobile-phone-link"
                className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0D4B56] text-[#F3E096]"
                aria-label={`Call Hope Giver at ${ORG.phoneDisplay}`}
              >
                <Phone size={20} />
              </a>
              <a
                href={ORG.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0D4B56] text-[#F3E096]"
                aria-label="Hope Giver on Instagram"
              >
                <Instagram size={20} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
